import { useState } from "react";
import { NavLink as RouterNavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { updateFormattingPreferences } from "@/lib/api";
import { S } from "@/lib/strings";
import { useTheme } from "@/hooks/use-theme";
import { getFormattingSettings, saveFormattingSettings, useFormattingSettings } from "@/lib/format";
import {
  LayoutDashboard,
  Search,
  AlertTriangle,
  Shield,
  ArrowLeftRight,
  CreditCard as CreditCardIcon,
  Receipt,
  LayoutGrid,
  Settings,
  UserCircle,
  UserPlus,
  LogOut,
  Sun,
  Moon,
  Monitor,
  PanelLeftClose,
  PanelLeft,
  LifeBuoy,
  Eye,
  EyeOff,
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const investigationItems = [
  { to: "/findings", label: S.NAV_FINDINGS, icon: Search },
  { to: "/suspicious", label: "Suspicious Transactions", icon: AlertTriangle },
  { to: "/payees", label: S.NAV_PAYEES, icon: Shield },
];

const budgetItems = [
  { to: "/accounts", label: S.NAV_TRANSACTIONS, icon: Receipt },
  { to: "/money-flow", label: "Money Flow", icon: ArrowLeftRight },
  { to: "/monthly-payments", label: "Monthly Payments", icon: CreditCardIcon },
  { to: "/categories", label: "Categories", icon: LayoutGrid },
];

const settingsAccountItems = [
  { to: "/settings", label: "Settings", icon: Settings },
  { to: "/account", label: "Account", icon: UserCircle },
  { to: "/support", label: "Support", icon: LifeBuoy },
];

const themeOptions = [
  { value: "light" as const, icon: Sun, label: "Light" },
  { value: "dark" as const, icon: Moon, label: "Dark" },
  { value: "system" as const, icon: Monitor, label: "System" },
];

export default function AppSidebar() {
  const { user, agencyContext, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, setTheme } = useTheme();
  const formattingSettings = useFormattingSettings();
  const { state, toggleSidebar } = useSidebar();
  const collapsed = state === "collapsed";
  const [showHideConfirm, setShowHideConfirm] = useState(false);

  const handleSignOut = async () => {
    await signOut();
    navigate("/login");
  };

  const current = themeOptions.find((t) => t.value === theme) || themeOptions[2];
  const CurrentIcon = current.icon;
  const AmountsIcon = formattingSettings.hideAmounts ? EyeOff : Eye;
  const amountVisibilityLabel = formattingSettings.hideAmounts ? S.SIDEBAR_SHOW_AMOUNTS : S.SIDEBAR_HIDE_AMOUNTS;

  const isActive = (to: string) =>
    location.pathname === to || location.pathname.startsWith(to + "/");

  const applyAmountVisibility = async (hide: boolean) => {
    const current = getFormattingSettings();
    const next = { ...current, hideAmounts: hide };
    saveFormattingSettings(next);
    try {
      await updateFormattingPreferences({
        currency: next.currency,
        currency_placement: next.currencyPlacement,
        number_format: next.numberFormat,
        date_format: next.dateFormat,
        hide_amounts: next.hideAmounts,
      });
      window.location.reload();
    } catch {
      saveFormattingSettings(current);
      toast.error("Failed to save preference");
    }
  };

  const handleAmountVisibilityToggle = () => {
    if (formattingSettings.hideAmounts) {
      void applyAmountVisibility(false);
    } else {
      setShowHideConfirm(true);
    }
  };

  const renderNavGroup = (
    label: string,
    items: typeof investigationItems
  ) => (
    <SidebarGroup>
      <SidebarGroupLabel>{label}</SidebarGroupLabel>
      <SidebarGroupContent>
        <SidebarMenu>
          {items.map(({ to, label: itemLabel, icon: Icon }) => (
            <SidebarMenuItem key={to}>
              <SidebarMenuButton asChild tooltip={itemLabel} isActive={isActive(to)}>
                <RouterNavLink to={to}>
                  <Icon className="h-4 w-4 shrink-0" />
                  <span>{itemLabel}</span>
                </RouterNavLink>
              </SidebarMenuButton>
            </SidebarMenuItem>
          ))}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );

  return (
    <>
    <Sidebar collapsible="icon">
      <SidebarHeader className="border-b border-border p-3">
        <div className="flex items-center gap-3">
          {user?.photoURL ? (
            <img src={user.photoURL} alt={user.displayName ?? ""} className="h-8 w-8 shrink-0 rounded-full object-cover" />
          ) : (
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-semibold">
              {(user?.displayName || user?.email || "U")[0].toUpperCase()}
            </div>
          )}
          {!collapsed && (
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-foreground">
                {user?.displayName || user?.email || "User"}
              </p>
              {agencyContext && (
                <p className="truncate text-xs text-muted-foreground">
                  {agencyContext.agency_name}
                </p>
              )}
            </div>
          )}
        </div>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton asChild tooltip={S.NAV_DASHBOARD} isActive={isActive("/dashboard")}>
                  <RouterNavLink to="/dashboard">
                    <LayoutDashboard className="h-4 w-4 shrink-0" />
                    <span>{S.NAV_DASHBOARD}</span>
                  </RouterNavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        {renderNavGroup("Investigation", investigationItems)}
        {renderNavGroup("Budget", budgetItems)}
        {renderNavGroup("Settings & Account", settingsAccountItems)}

        {agencyContext?.user_role === "admin" && (
          <SidebarGroup>
            <SidebarGroupLabel>Admin</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild tooltip={S.NAV_ADMIN} isActive={isActive("/admin")}>
                    <RouterNavLink to="/admin">
                      <UserPlus className="h-4 w-4 shrink-0" />
                      <span>{S.NAV_ADMIN}</span>
                    </RouterNavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>

      <SidebarFooter className="border-t border-border p-2 space-y-1">
        <button
          type="button"
          onClick={handleAmountVisibilityToggle}
          aria-label={amountVisibilityLabel}
          title={amountVisibilityLabel}
          className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-xs font-semibold transition-colors ${
            formattingSettings.hideAmounts
              ? "bg-primary/10 text-primary hover:bg-primary/15"
              : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
          }`}
        >
          <AmountsIcon className="h-3.5 w-3.5 shrink-0" />
          {!collapsed && <span>{amountVisibilityLabel}</span>}
        </button>

        {collapsed ? (
          <button
            onClick={toggleSidebar}
            className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-md text-muted-foreground hover:text-foreground transition-colors"
            title="Expand sidebar"
            aria-label="Expand sidebar"
          >
            <PanelLeft className="h-3.5 w-3.5" />
          </button>
        ) : (
          <div className="flex items-center justify-between">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex min-h-[44px] items-center gap-2 rounded-md px-2 text-xs font-semibold text-sidebar-foreground hover:text-sidebar-primary transition-colors"
                  aria-label={`Theme: ${current.label}`}
                >
                  <CurrentIcon className="h-3.5 w-3.5 shrink-0" />
                  <span>{current.label}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" side="top" className="w-36">
                {themeOptions.map(({ value, icon: ThIcon, label }) => (
                  <DropdownMenuItem
                    key={value}
                    onClick={() => setTheme(value)}
                    className={`gap-2 ${theme === value ? "bg-accent" : ""}`}
                  >
                    <ThIcon className="h-3.5 w-3.5" />
                    {label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <button
              onClick={toggleSidebar}
              className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-md text-muted-foreground hover:text-foreground transition-colors"
              title="Collapse sidebar"
              aria-label="Collapse sidebar"
            >
              <PanelLeftClose className="h-3.5 w-3.5" />
            </button>
          </div>
        )}

        <SidebarMenuButton asChild tooltip={S.BTN_LOGOUT}>
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start gap-2 text-muted-foreground"
            onClick={handleSignOut}
          >
            <LogOut className="h-4 w-4" />
            {!collapsed && <span>{S.BTN_LOGOUT}</span>}
          </Button>
        </SidebarMenuButton>
      </SidebarFooter>
    </Sidebar>

    <AlertDialog open={showHideConfirm} onOpenChange={setShowHideConfirm}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Hide all amounts?</AlertDialogTitle>
          <AlertDialogDescription>
            Amounts in transactions, money flow, payments and categories will be masked.
            Findings data is not masked.
            The page will refresh to apply the change.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={() => void applyAmountVisibility(true)}>
            OK
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}
